import { buildCalendarUrl, eventToCalendarLinkParts } from "@/components/calendars/calendar-url.ts";
import { getCurrentTimezone } from "@/lib/user.ts";
import { cn } from "@/lib/utils.ts";
import type { ReactNode } from "react";
import { Link } from "wouter";

/**
 * Linkified event date string. Emits the canonical calendar focus URL for the
 * event's day; where a calendar is mounted the enclosing
 * `CalendarFocusNavBoundary` moves it in place, and everywhere else the link
 * loads the `/calendar` page on that day.
 *
 * If `isAllDay` is false and `endDateMs` is provided, the event's exact time
 * range rides along and is highlighted on the grid AND mirrored as a chip in the
 * omnibox (both fall out of the shared range selection). All-day events and
 * events without an end timestamp skip the range — the calendar just opens on
 * the day.
 *
 * `children` is the visible text. Each artifact keeps its own range
 * formatter and passes the formatted string in.
 */
export const EventDateLink = ({
  startDateMs,
  endDateMs,
  isAllDay,
  className,
  children,
}: {
  startDateMs: number;
  endDateMs?: number;
  isAllDay?: boolean;
  className?: string;
  children: ReactNode;
}) => {
  const href = buildCalendarUrl(
    eventToCalendarLinkParts(startDateMs, endDateMs, isAllDay, getCurrentTimezone()),
  );

  return (
    <Link
      href={href}
      aria-label={typeof children === "string" ? `Open calendar: ${children}` : "Open calendar day"}
      // These render inside clickable artifact cards, whose own click handler
      // would otherwise fire alongside the navigation.
      onClick={(e) => e.stopPropagation()}
      className={cn("link inline text-left", className)}
    >
      {children}
    </Link>
  );
};
